import { useState, useRef, useEffect, useCallback } from 'react';
import { X } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import {
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getNotificationLink,
} from '../services/notificationService';
import { useNavigate } from 'react-router-dom';
import '../styles/NotificationTray.css';

function timeAgo(unixSeconds) {
  if (!unixSeconds) return '';
  const seconds = Math.floor(Date.now() / 1000) - unixSeconds;
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

function NotifIcon({ type }) {
  // Agent/AI suggestions
  if (type === 'boosters' || type === 'relationship tips') {
    return (
      <div className="notif-agent-avatar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="#fff"/>
        </svg>
      </div>
    );
  }
  // Circle events
  if (type === 'circle_event') {
    return (
      <div className="notif-circle-avatar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="18" rx="2" stroke="#fff" strokeWidth="1.6"/>
          <path d="M16 2v4M8 2v4M3 10h18" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      </div>
    );
  }
  // Circle messages
  if (type === 'circle_message') {
    return (
      <div className="notif-circle-avatar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M21 12c0 4.4-4 8-9 8a10 10 0 0 1-3.5-.6L3 21l1.7-4.6A7.5 7.5 0 0 1 3 12c0-4.4 4-8 9-8s9 3.6 9 8Z" stroke="#fff" strokeWidth="1.6"/>
        </svg>
      </div>
    );
  }
  // Messages
  if (type === 'message') {
    return (
      <div className="notif-msg-avatar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10Z" stroke="#fff" strokeWidth="1.6"/>
        </svg>
      </div>
    );
  }
  // Default bell
  return (
    <div className="notif-default-avatar">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    </div>
  );
}

export default function NotificationTray({ open, onClose, anchorRef, onUnreadCountChange }) {
  const [tab, setTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const ref = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Subscribe to real notifications
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeToNotifications(
      user.uid,
      (items) => setNotifications(items),
      (err) => console.error('Notification subscription error:', err)
    );
    return unsub;
  }, [user?.uid]);

  const handleOutsideClick = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target) &&
        (!anchorRef?.current || !anchorRef.current.contains(e.target))) {
      onClose();
    }
  }, [onClose, anchorRef]);

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, handleOutsideClick, handleKey]);

  const unreadCount = notifications.filter(n => !n.readStatus).length;
  const items = tab === 'all' ? notifications : notifications.filter(n => !n.readStatus);

  // Surface the unread count to the parent (WebTopBar bell badge, PPL-157) so it
  // can reuse this one subscription instead of opening its own.
  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const handleMarkAllRead = async () => {
    if (!user?.uid) return;
    try {
      await markAllNotificationsRead(user.uid);
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  const handleNotificationClick = async (n) => {
    if (!user?.uid) return;
    if (!n.readStatus) {
      try {
        await markNotificationRead(user.uid, n.id);
      } catch (err) {
        console.error('Failed to mark read:', err);
      }
    }
    const link = getNotificationLink(n);
    onClose();
    navigate(link);
  };

  return (
    <div ref={ref} className={`notif-tray ${open ? 'open' : ''}`}>
      {/* Header */}
      <div className="notif-tray-header">
        <div className="notif-tray-header-left">
          <div className="notif-tray-title">Notifications</div>
          {unreadCount > 0 && (
            <span className="notif-tray-count">{unreadCount} new</span>
          )}
        </div>
        <button className="notif-tray-close" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      {/* Tabs + Mark all read */}
      <div className="notif-tray-tabs">
        {[
          { id: 'all', label: 'All', count: notifications.length },
          { id: 'unread', label: 'Unread', count: unreadCount },
        ].map(p => (
          <button
            key={p.id}
            className={`notif-tab ${tab === p.id ? 'active' : ''}`}
            onClick={() => setTab(p.id)}
          >
            <span>{p.label}</span>
            <span className="notif-tab-count">{p.count}</span>
          </button>
        ))}
        <div className="notif-tray-spacer" />
        {unreadCount > 0 && (
          <button className="notif-mark-all" onClick={handleMarkAllRead}>Mark all read</button>
        )}
      </div>

      {/* Notification list */}
      <div className="notif-tray-list">
        {items.map(n => (
          <div
            key={n.id}
            role="button"
            tabIndex={0}
            className={`notif-row ${!n.readStatus ? 'unread' : ''}`}
            onClick={() => handleNotificationClick(n)}
          >
            {!n.readStatus && <span className="notif-unread-dot" />}
            <NotifIcon type={n.type} />
            <div className="notif-row-content">
              <div className="notif-row-header">
                <div className="notif-row-title">{n.title}</div>
                <div className="notif-row-when">{timeAgo(n.createdDate)}</div>
              </div>
              <div className="notif-row-body">{n.description}</div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="notif-empty">
            {tab === 'unread' ? 'All caught up.' : 'No notifications yet.'}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="notif-tray-footer">
        <button className="notif-footer-btn" onClick={() => { onClose(); navigate('/settings'); }}>
          <span>Notification settings</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
